import { App } from "antd";
import { useTranslation } from "react-i18next";
import type { Reservation } from "../../../api/reservations";
import { useCreateReservation, useUpdateReservation } from "../../../hooks/useReservations";
import { getApiErrorMessage } from "../../../utils/translations";
import type { NewReservationForm } from "./useNewReservationForm";

// Полетата на POST /reservations с превод на 422 грешката (BE валидаторите
// са с твърд български текст — в английския UI се показва преводът по полето).
const VALIDATED_FIELDS = new Set([
  "startAt",
  "durationMinutes",
  "guests",
  "guestName",
  "phone",
  "note",
  "tableIds",
]);

/** FastAPI 422: detail е списък с loc ["body", "<поле>"] — ключът на превода. */
const validationField = (error: unknown): string | null => {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (!Array.isArray(detail) || detail.length === 0) return null;
  const loc = (detail[0] as { loc?: unknown })?.loc;
  const field = Array.isArray(loc) ? loc[1] : null;
  return typeof field === "string" && VALIDATED_FIELDS.has(field) ? field : null;
};

/** Секцията „Маси" на формата — накъде се скролва, когато няма избрана маса. */
export const TABLES_SECTION_ID = "reservation-tables";

/**
 * Грешките се появяват след следващото рисуване — тогава първото невалидно
 * поле влиза в екрана и взима фокуса, а без такова — секцията „Маси".
 * Иначе на компютър „Създай" изглежда, че не прави нищо: формата скролва, а
 * грешките са под сгъвката.
 */
const revealFirstProblem = () => {
  requestAnimationFrame(() => {
    const invalid = document.querySelector<HTMLElement>('[aria-invalid="true"]');
    const target = invalid ?? document.getElementById(TABLES_SECTION_ID);
    target?.scrollIntoView({ block: "center", behavior: "smooth" });
    invalid?.focus({ preventScroll: true });
  });
};

/**
 * Записът на „Нова резервация" / „Редакция на резервация" (POST или PATCH) —
 * общ за панела в „Резервации" и модала в „Таблица на резервациите", за да са
 * еднакви валидациите и съобщенията. „Създай" изглежда изключен, докато
 * формата не е готова; кликът по него показва грешките под полетата.
 */
export const useSaveReservation = (form: NewReservationForm, onSaved: (reservation: Reservation) => void) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const createMutation = useCreateReservation();
  const updateMutation = useUpdateReservation();
  const isSaving = createMutation.isPending || updateMutation.isPending;
  const { editing } = form;

  const save = async () => {
    if (isSaving) return;
    if (!form.canSubmit) {
      form.revealErrors();
      revealFirstProblem();
      return;
    }
    try {
      const saved = editing
        ? await updateMutation.mutateAsync({ id: editing.id, data: form.payload() })
        : await createMutation.mutateAsync(form.payload());
      message.success(t(editing ? "reservations.form.saved" : "reservations.form.created"));
      onSaved(saved);
    } catch (error) {
      const field = validationField(error);
      if (field === "phone" || field === "guestName") {
        form.revealErrors();
        revealFirstProblem();
      }
      message.error(
        field
          ? t(`reservations.form.errors.${field}`)
          : getApiErrorMessage(
              error,
              t(editing ? "reservations.form.saveFailed" : "reservations.form.createFailed"),
            ),
      );
    }
  };

  return { save, isSaving };
};
